/**
 * Nextcloud folder picker (#98): browse the connected account (folders only)
 * and add one folder the organization may see. Built like the Move dialog:
 * Bootstrap modal, shared dialog queue, breadcrumbs, all text via textContent.
 * The proxy decides what may be added (`canExpose`); the picker only shows it.
 */

import { enqueueDialog } from './dialog-queue';
import { queuedModal } from './queued-modal';
import { nextcloudErrorText } from './nextcloud-error-text';
import {
  isIntegrationError,
  type default as IntegrationsService,
  type NextcloudBrowse,
  type NextcloudBrowseFolder,
  type NextcloudStatus,
} from '../core/integrations-service';
import type { TranslationParams } from '../i18n/ui-manager.types';

export interface NextcloudFolderPickerOptions {
  service: IntegrationsService;
  t: (key: string, params?: TranslationParams) => string;
}

const K = 'modules.integrations.nextcloud';
let instanceCounter = 0;

/** Resolves the status after a folder was added, or null when closed. */
export function nextcloudFolderPicker(
  options: NextcloudFolderPickerOptions
): Promise<NextcloudStatus | null> {
  return new Promise<NextcloudStatus | null>((resolve) => {
    enqueueDialog((done) => {
      try {
        openPicker(options, resolve, done);
      } catch {
        resolve(null);
        done();
      }
    });
  });
}

function openPicker(
  { service, t }: NextcloudFolderPickerOptions,
  resolve: (value: NextcloudStatus | null) => void,
  done: () => void
): void {
  const id = `nextcloud-picker-${++instanceCounter}`;

  const modal = document.createElement('div');
  modal.className = 'modal fade';
  modal.id = id;
  modal.tabIndex = -1;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${id}-title`);

  const content = document.createElement('div');
  content.className = 'modal-content';

  const header = document.createElement('div');
  header.className = 'modal-header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'modal-title fs-5';
  titleEl.id = `${id}-title`;
  titleEl.textContent = t(`${K}.picker.title`);
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn-close';
  close.setAttribute('aria-label', t(`${K}.cancel`));
  header.append(titleEl, close);

  const body = document.createElement('div');
  body.className = 'modal-body';
  const crumbs = document.createElement('nav');
  crumbs.className = 'nextcloud-picker__crumbs';
  crumbs.setAttribute('aria-label', t(`${K}.picker.root`));
  const list = document.createElement('ul');
  list.className = 'nextcloud-picker__list list-unstyled mb-0';
  const note = document.createElement('p');
  note.className = 'text-muted small mb-0';
  const error = document.createElement('div');
  error.className = 'invalid-feedback d-block';
  error.setAttribute('role', 'alert');
  error.dataset.pickerError = '';
  body.append(crumbs, list, note, error);

  const footer = document.createElement('div');
  footer.className = 'modal-footer';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn btn-outline-secondary';
  cancel.dataset.pickerAction = 'cancel';
  cancel.textContent = t(`${K}.cancel`);
  footer.append(cancel);

  content.append(header, body, footer);
  const dialog = document.createElement('div');
  dialog.className =
    'modal-dialog modal-dialog-centered modal-dialog-scrollable';
  dialog.appendChild(content);
  modal.appendChild(dialog);
  document.body.appendChild(modal);

  let settled = false;
  let busy = false;
  let loadId = 0;

  const settle = (value: NextcloudStatus | null): void => {
    if (settled) return;
    settled = true;
    resolve(value);
  };
  const { hide, show } = queuedModal(modal, {
    done,
    onClosed: () => settle(null),
  });
  const finish = (value: NextcloudStatus | null): void => {
    settle(value);
    hide();
  };
  const setError = (text: string): void => {
    error.textContent = text;
  };

  /** A needs-reconnect answer closes the picker with the fresh status. */
  const failed = (e: unknown): void => {
    if (isIntegrationError(e) && e.code === 'NEEDS_RECONNECT') {
      service
        .status()
        .catch(() => null)
        .then((status) => finish(status));
      return;
    }
    setError(nextcloudErrorText(e, t));
  };

  const crumb = (
    label: string,
    path: string,
    current: boolean
  ): HTMLElement => {
    if (current) {
      const span = document.createElement('span');
      span.dataset.pickerCrumb = '';
      span.setAttribute('aria-current', 'page');
      span.textContent = label;
      return span;
    }
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-link p-0 align-baseline';
    b.dataset.pickerCrumb = '';
    b.textContent = label;
    b.addEventListener('click', () => void load(path));
    return b;
  };

  const mountLabel = (folder: NextcloudBrowseFolder): string | null =>
    folder.mountType === 'own' ? null : t(`${K}.picker.${folder.mountType}`);

  const render = (listing: NextcloudBrowse): void => {
    crumbs.replaceChildren();
    const parts: [string, string][] = [
      [t(`${K}.picker.root`), ''],
      ...listing.parents.map((p): [string, string] => [p.name, p.path]),
    ];
    parts.forEach(([label, path], i) => {
      if (i > 0) crumbs.append(document.createTextNode(' / '));
      crumbs.append(crumb(label, path, i === parts.length - 1));
    });

    list.replaceChildren();
    for (const folder of listing.folders) {
      const item = document.createElement('li');
      item.className =
        'nextcloud-picker__item d-flex align-items-center gap-2 py-1';
      item.dataset.pickerFolder = folder.name;

      const openButton = document.createElement('button');
      openButton.type = 'button';
      openButton.className = 'btn btn-link p-0 text-start flex-grow-1';
      openButton.dataset.pickerAction = 'open';
      openButton.setAttribute(
        'aria-label',
        t(`${K}.picker.open`, { name: folder.name })
      );
      openButton.textContent = folder.name;
      openButton.addEventListener('click', () => void load(folder.path));
      item.append(openButton);

      const mount = mountLabel(folder);
      if (mount) {
        const badge = document.createElement('span');
        badge.className = 'badge text-bg-light';
        badge.textContent = mount;
        item.append(badge);
      }

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'btn btn-sm btn-outline-primary';
      add.dataset.pickerAction = 'add';
      add.textContent = t(
        folder.exposed ? `${K}.picker.added` : `${K}.picker.add`
      );
      add.disabled = !folder.canExpose;
      add.addEventListener('click', () => {
        if (busy) return;
        busy = true;
        setError('');
        service
          .addFolder(folder.path)
          .then((status) => {
            busy = false;
            finish(status);
          })
          .catch((e: unknown) => {
            busy = false;
            failed(e);
          });
      });
      item.append(add);
      list.append(item);
    }
    note.textContent =
      listing.folders.length === 0
        ? t(`${K}.picker.empty`)
        : listing.truncated
          ? t(`${K}.picker.truncated`)
          : '';
  };

  const load = async (path: string): Promise<void> => {
    const id = ++loadId;
    setError('');
    try {
      const listing = await service.browse(path);
      if (id === loadId && !settled) render(listing);
    } catch (e) {
      if (id === loadId && !settled) failed(e);
    }
  };

  cancel.addEventListener('click', () => finish(null));
  close.addEventListener('click', () => finish(null));
  modal.addEventListener('hide.bs.modal', (event) => {
    if (busy) {
      event.preventDefault();
      return;
    }
    settle(null);
  });

  if (show()) void load('');
}
